<template>
  <aside class="development-strategies-page">
    <div>
      <DocumentPageTitle
        :word-count="totalWords"
        :word-limit="wordLimit"
        title="Development Strategies"
      />

      <template v-if="planContentKey.length > 0">
        <DefaultSearch
          class="p-0 mb-6 [&_.input-field]:!min-w-[420px] [&_form]:!gap-0"
          btn-class="btn-solid btn-primary !rounded-none"
          placeholder="Type to search development strategies"
          :archiveable="false"
          @search="searchParams = $event"
        />

        <AppTable>
          <AppTableHeader>
            <th class="!border-r-0">
              Name of Strategy
            </th>

            <th class="w-[100px]" />
          </AppTableHeader>

          <AppTableBody
            :loading="false"
            :colspan="2"
            :no-results="filteredStrategies.length === 0"
            :no-results-text="searchParams.search ? 'No strategies matched your search' : 'No strategies'"
          >
            <tr
              v-for="(strategy, index) in filteredStrategies"
              :key="strategy.id || index"
            >
              <td class="!border-r-0">
                {{ strategy.name }}
              </td>

              <td>
                <div class="flex items-center justify-end">
                  <DocumentActions
                    subject="strategy"
                    @edit-clicked="formModal.open('strategy', strategy)"
                    @delete-clicked="deleteModal.open('strategy', strategy)"
                  />
                </div>
              </td>
            </tr>
          </AppTableBody>
        </AppTable>
      </template>

      <AppButton
        class="btn-dashed btn-outline btn-secondary mt-4 mb-10"
        @click="formModal.open('strategy', strategySchema)"
      >
        <FontAwesomeIcon :icon="faPlus" />
        Add a Development Strategy
      </AppButton>
    </div>

    <AppModal
      v-if="!!formModal.type"
      size="5xl"
      class="[&_.modal-body]:h-full [&_.modal-body]:!p-0"
      :title="`${formModal.isEditing ? 'Edit' : 'Add'} Development Strategy`"
      :disabled="formModal.processing"
      @close="handleCancel"
    >
      <template #body>
        <DevelopmentStrategyForm
          v-model="formModal.data"
          :error="error"
          @submit="handleSave"
        />
      </template>

      <template #footer>
        <ButtonSubmitRow
          :processing="formModal.processing"
          @submit-clicked="handleSave"
          @cancel-clicked="handleCancel"
        />
      </template>
    </AppModal>

    <ConfirmationModal
      v-if="!!deleteModal.type"
      intent="danger"
      :title="`Delete ${deleteModal.type}`"
      :processing="deleteModal.processing"
      @close="deleteModal.close"
      @confirm="handleDelete"
    >
      Are you sure you want to delete the strategy <strong>{{ deleteModal.data.name }}</strong>?
    </ConfirmationModal>
  </aside>
</template>

<script setup>
import { ref, computed } from 'vue';

import { AppButton, AppModal } from '@/components/app';
import { ButtonSubmitRow } from '@/components/buttons';
import { DefaultSearch } from '@/components/forms/search';
import { AppTable, AppTableBody, AppTableHeader } from '@/components/tables';
import { ConfirmationModal } from '@/components/modals';
import { DocumentPageTitle, DocumentActions } from '@/components/plan-document/components';
import { DevelopmentStrategyForm } from '@/components/plan-document/forms';

import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

import { useModal } from '@/composables';
import { usePlanStore } from '@/store';

const planStore = usePlanStore();
const wordLimit = 12000;
const error = ref(null);

const planContentKey = computed(() => {
  return planStore.content?.developmentStrategies || {};
});

/**
 * Strategy editors live in a modal (not always mounted), so word counts can't be driven by
 * TinyMCE events. Instead, derive totalWords directly from store data on each change.
 *
 * Strips HTML tags from TinyMCE output and returns the word count of the remaining text.
 * @param {string|null} text
 * @returns {number}
 */
const countWords = (text) => {
  if (!text) return 0;
  return text.replace(/<[^>]*>/g, ' ').trim().split(/\s+/).filter(Boolean).length;
};

// Sums words across the name and description of every development strategy.
const totalWords = computed(() => {
  const strategies = planStore.content?.developmentStrategies || [];
  return strategies.reduce((sum, s) => sum + countWords(s.name) + countWords(s.description), 0);
});

// Search State
const searchParams = ref({
  search: null,
});

// Modals
const formModal = useModal();
const deleteModal = useModal();

const strategySchema = { id: null, name: '' };

// Computed Logic for Filtering
const filteredStrategies = computed(() => {
  const query = searchParams.value.search?.toLowerCase().trim();
  const strategies = planContentKey.value;

  if (!query) return strategies;

  return strategies.filter(strategy => strategy.name?.toLowerCase().includes(query));
});

const handleSave = () => {
  const { data, isEditing } = formModal;
  formModal.processing = true;

  // This indicates that no name has been entered
  if (!data.name) {
    error.value = 'This is a required field';
    formModal.processing = false;

    return;
  }

  if (isEditing) {
    const index = planContentKey.value.findIndex(strategy => strategy.id === data.id);

    if (index !== -1) {
      planContentKey.value[index] = { ...data };
    }
  } else {
    const newStrategy = {
      ...data,
      id: Date.now(),
    };

    planContentKey.value.push(newStrategy);
  }

  error.value = null;
  formModal.close();
};

const handleDelete = () => {
  const { data } = deleteModal;
  deleteModal.processing = true;

  const index = planContentKey.value.findIndex(s => s.id === data.id);
  planContentKey.value.splice(index, 1);

  deleteModal.close();
};

const handleCancel = () => {
  error.value = null;
  formModal.close();
};

</script>
